import {
  Badge,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, { Suspense, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import { LoggedInSkeleton } from "src/Components/Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "src/Components/Common/TopNavBar";
import { userContext } from "src/Components/Contexts/userContext";
import ReactTable, { ColDef } from "src/Components/ReactTable/ReactTable";
import { LoadingPage } from "../../PartnerCommunication/MsmePartnerPortal/PartnerPortal.common";

import { Download, FilterList } from "@mui/icons-material";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import saveAs from "file-saver";
import moment from "moment";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import useFetch from "src/Components/Common/useFetch";
import { UserRole } from "src/entity/models/FrontendActor";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { formatMoney, ToDineroObj } from "src/Utils/MoneyUtils";
import { uiLoggerNamesInvoiceRejection } from "src/Utils/Recon/UiLogger/Constants";
import uiLogger from "src/Utils/UiLogger";
import { IssueCategorizationOptions } from "../../VendorPortal/RejectedInvoicesVendorPortal";
import BulkActionOfRejectedInvoices from "./BulkActionOfRejectedInvoices";
import EditDetailsOfRejectedInvoices from "./EditDetailsOfRejectedInvoices";
import FilterOfRejectedInvoices from "./FilterOfRejectedInvoices";
import HistoryOfRejectedInvoices from "./HistoryOfRejectedInvoices";
import RejectedInvoicesFiltersBars from "./RejectedInvoicesFiltersBars";
import RejectedInvoicesTakeActionModal from "./RejectedInvoicesTakeActionModal";

export const InternalResolutionStatus = {
  Unassigned: "Unassigned",
  InProgress: "In-Progress",
  PendingApproval: "Pending Approval",
  UpdatedInViniculum: "Updated In Viniculum",
  InvoiceProcessing: "Invoice Processing",
  RejectedAgain: "Rejected Again",
  Posted: "Posted",
};
export const RejectedInvoicesPriority = {
  High: "HIGH",
  Medium: "MEDIUM",
  Low: "LOW",
};
export enum InstamartInternalProcessRole {
  APPROVER = "approver",
  CHECKER = "checker",
  MAKER = "maker",
  CHECKER_APPROVER = "checkerApprover",
}
export enum AgingCategories {
  ZERO_TO_FIVE = "0-5",
  SIX_TO_TEN = "6-10",
  ELEVEN_TO_FIVETEEN = "11-15",
  SIXTEEN_TO_THIRTY = "16-30",
  THIRTY_ONE_TO_FORTY_FIVE = "31-45",
  FORTY_SIX_TO_SIXTY = "46-60",
  SIXTY_ONE_TO_NINETY = "61-90",
  NINETY_ONE_TO_TWENTY = "91-120",
  ONE_TWENTY_ONE_TO_ONE_EIGHTY = "121-180",
  ONE_EIGHTY_PLUS = ">180",
}

export enum InwardingAgingCategories {
  ZERO_TO_SEVEN = "0-7",
  EIGHT_TO_FIFTEEN = "8-15",
  SIXTEEN_TO_THIRTY = "16-30",
  THIRTY_ONE_PLUS = ">30",
}

// export type ReceivedQF = "all" | "Rejected again";
export const QuickFilterOptions = {
  All: "All",
  RejectedAgain: "Rejected again",
  HighPriority: "High Priority",
} as const;
export type RejectedInvoicesQF = typeof QuickFilterOptions[keyof typeof QuickFilterOptions];

// Page size must stay within the rowsPerPageOptions the table offers, and the server caps it at 200.
const DEFAULT_PAGINATION = { pageIndex: 0, pageSize: 100 };

const RejectedInvoices = () => {
  const { actor } = useContext(userContext);
  const role =
    actor?.userRole === UserRole.DRMaker
      ? InstamartInternalProcessRole.MAKER
      : actor?.userRole === UserRole.DRChecker
      ? InstamartInternalProcessRole.CHECKER
      : actor?.userRole === UserRole.DRApprover || actor.userRole === UserRole.ReconDRApprover
      ? InstamartInternalProcessRole.APPROVER
      : null;
  const location = useLocation<any>();
  // const quickFilterOptions = Object.values(QuickFilterOptions) as RejectedInvoicesQF[];
  // Create role-specific filter options
  const quickFilterOptions = useMemo(() => {
    if (actor?.userRole === UserRole.DRMaker) {
      return [QuickFilterOptions.All, QuickFilterOptions.RejectedAgain];
    } else {
      // For DRChecker and DRApprover
      return [QuickFilterOptions.All, QuickFilterOptions.HighPriority];
    }
  }, [actor?.userRole]);
  const [tabValue, setTabValue] = useState(0);
  const [loader, setLoader] = useState(true);
  const [tableLoader, setTableLoader] = useState(true);

  const [rowSelection, setRowSelection] = useState({});

  const [clearRows, setClearRows] = useState(false);
  const [openRejectedInvoicesResolveModal, setOpenRejectedInvoicesResolveModal] = useState(false);
  const selectedRowForRejectedInvoicesResolveModal = useRef(null);
  const [openEditDetailsOfRejectedInvoices, setOpenEditDetailsOfRejectedInvoices] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] = useState(null);
  const [openHistoryOfRejectedInvoices, setOpenHistoryOfRejectedInvoices] = useState(false);
  const [selectedRowForHistory, setSelectedRowForHistory] = useState(null);
  const [openBulkActionOfRejectedInvoices, setOpenBulkActionOfRejectedInvoices] = useState(false);
  const [openFilterOfRejectedInvoices, setOpenFilterOfRejectedInvoices] = useState(false);
  const [rowsDataRejectedInvoicesOpen, setRowsDataRejectedInvoicesOpen] = useState([]);
  const [rowsDataRejectedInvoicesClose, setRowsDataRejectedInvoicesClose] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [isDownloadInvoiceReport, setIsDownloadInvoiceReport] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [filters, setFilters] = useState([]);
  // Server-side paging: the closed list alone is >600k rows, so the table only ever holds one page.
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  // const [debouncedFilters] = useDebounce(filters, 500); // 500ms delay

  const [openInvoiceCount, setOpenInvoiceCount] = useState(0);
  const [closeInvoiceCount, setCloseInvoiceCount] = useState();
  const [applyFilterLoader, setApplyFilterLoader] = useState(false);
  const [allDropDownValues, setAllDropDownValues] = useState(null);

  const columnDefinitionRejectedInvoices = React.useMemo(
    (): ColDef<any>[] => [
      {
        header: "Issue categorization",
        id: "issueCategory",
        accessorFn: (row) => row.issueCategory || "",
        filterVariant: "multi-select",
        filterSelectOptions: Object.values(IssueCategorizationOptions),
        size: 300,
      },
      {
        header: "Resolution Status",
        id: "issueStatus",
        accessorFn: (row) => row.issueStatus || "",
        filterVariant: "multi-select",
        filterSelectOptions: [
          { label: "In Progress", value: InternalResolutionStatus.InProgress },
          { label: "Pending Approval", value: InternalResolutionStatus.PendingApproval },
          { label: "Invoice Processing", value: InternalResolutionStatus.InvoiceProcessing },
          { label: "Posted", value: InternalResolutionStatus.Posted },
          { label: "Rejected Again", value: InternalResolutionStatus.RejectedAgain },
          { label: "Updated In Viniculum", value: InternalResolutionStatus.UpdatedInViniculum },
          { label: "Unassigned", value: InternalResolutionStatus.Unassigned },
        ],
        size: 250,
        Cell: ({ cell }) => {
          const issueStatus = cell.getValue() as string;
          let chipColor: "primary" | "warning" | "success" | "default" | "secondary" | "error" | "info" = "default";
          if (issueStatus === InternalResolutionStatus.InProgress) chipColor = "primary";
          else if (issueStatus === InternalResolutionStatus.PendingApproval) chipColor = "warning";
          else if (issueStatus === InternalResolutionStatus.InvoiceProcessing) chipColor = "success";
          else if (issueStatus === InternalResolutionStatus.Posted) chipColor = "success";
          else if (issueStatus === InternalResolutionStatus.RejectedAgain) chipColor = "error";
          else if (issueStatus === InternalResolutionStatus.UpdatedInViniculum) chipColor = "primary";
          else if (issueStatus === InternalResolutionStatus.Unassigned) chipColor = "default";

          return <Chip label={issueStatus} size="small" color={chipColor} variant="outlined" />;
        },
      },
      {
        header: "Vendor Name",
        id: "vendorName",
        accessorFn: (row) => row.vendorName || "",
        size: 350,
        Cell: ({ cell }) => {
          const text = cell.getValue() as string;

          return (
            <div
              style={{
                whiteSpace: "nowrap", // Prevent text from wrapping to the next line
                overflow: "hidden", // Hide overflowing text
                textOverflow: "ellipsis", // Add "..." to indicate truncated text
                maxWidth: "100%", // Ensure it respects the column size
              }}
              title={text} // Show full text on hover
            >
              {text}
            </div>
          );
        },
      },
      {
        header: "GRN Number",
        id: "grnNumber",
        accessorFn: (row) => row.grnNumber || "",
        size: 280,
      },
      {
        header: "GRN Amount",
        id: "grnAmount",
        accessorFn: (row) => row.grnAmount || "",
        size: 250,
        filterVariant: "range",
        filterFn: "betweenInclusive",
        Cell: ({ row }: any) => (
          <div
            className="textOverflow_hidden"
            title={row.original?.grnAmount ? formatMoney(ToDineroObj(row.original?.grnAmount)) : ""}
          >
            {row.original?.grnAmount ? formatMoney(ToDineroObj(row.original?.grnAmount)) : "-"}
          </div>
        ),
      },
      {
        header: "GRN Date",
        id: "grnDate",
        accessorFn: (row) => new Date(row.grnDate || null),
        size: 350,
        filterVariant: "date-range",
        Cell: ({ row }) => (
          <div className="textOverflow_hidden">
            {row.original.grnDate ? moment(row.original.grnDate)?.format("DD-MM-YYYY") : "-"}
          </div>
        ),
      },
      {
        header: "GST as per invoice",
        id: "gstNumberAsPerInvoice",
        accessorFn: (row) => row.gstNumberAsPerInvoice || "",
        size: 280,
      },
      {
        header: "GST Number",
        id: "gstNumber",
        accessorFn: (row) => row.gstNumber || "",
        size: 280,
      },
      {
        header: "Paid through",
        id: "paidThrough",
        accessorFn: (row) => row.paidThrough || "",
        size: 200,
      },
      {
        header: "Priority",
        id: "priority",
        accessorFn: (row) => row.priority || "",
        filterVariant: "multi-select",
        filterSelectOptions: Object.values(RejectedInvoicesPriority),
        size: 200,
      },
      { header: "Ageing", id: "ageing", accessorFn: (row) => row.aging || "", size: 250 },
      {
        header: "Dependency",
        id: "dependency",
        accessorFn: (row) => row.dependency || "",
        filterVariant: "multi-select",
        filterSelectOptions: ["Inwarding", "Finance", "Procurement", "Zoho Team"],
        size: 250,
      },
      {
        header: "Category",
        id: "category",
        accessorFn: (row) => row.category || "",
        filterVariant: "multi-select",
        filterSelectOptions: ["DSD", "Non DSD"],
        filterFn: (row, columnId, filterValue) => {
          // If no filters are applied, show all rows
          if (!filterValue || filterValue.length === 0) {
            return true;
          }
          // Otherwise, match exactly
          return filterValue.includes(row.getValue(columnId));
        },
        size: 250,
      },
      {
        header: "Outlet Name",
        id: "outletName",
        accessorFn: (row) => row.outletName || "",

        size: 300,
      },
      { header: "City", id: "city", accessorFn: (row) => row.city || "", size: 250 },
      {
        header: "Store Id",
        id: "outletId",
        accessorFn: (row) => (row.outletId !== undefined && row.outletId !== null ? row.outletId.toString() : ""),
        size: 250,
      },
      {
        header: "Item Type",
        id: "itemType",
        accessorFn: (row) => row.itemType || "",
        filterVariant: "multi-select",
        filterSelectOptions: ["FnV", "Non FnV"],
        filterFn: (row, columnId, filterValue) => {
          // If no filters are applied, show all rows
          if (!filterValue || filterValue.length === 0) {
            return true;
          }
          // Otherwise, match exactly
          return filterValue.includes(row.getValue(columnId));
        },
        size: 250,
      },
      {
        header: "Comments",
        id: "comments",
        accessorFn: (row) => row.comment || "",
        size: 300,
        Cell: ({ cell }) => {
          const text = cell.getValue() as string;

          return (
            <div
              style={{
                whiteSpace: "nowrap", // Prevent text from wrapping to the next line
                overflow: "hidden", // Hide overflowing text
                textOverflow: "ellipsis", // Add "..." to indicate truncated text
                maxWidth: "100%", // Ensure it respects the column size
                cursor: "pointer", // Indicate interactivity
              }}
              title={text} // Show full text on hover
            >
              {text}
            </div>
          );
        },
      },
      {
        header: "Last Comment Date",
        id: "lastCommentDate",
        accessorFn: (row) => new Date(row.lastCommentDate || null),
        size: 350,
        filterVariant: "date-range",

        Cell: ({ row }) => (
          <div className="textOverflow_hidden">
            {row.original.lastCommentDate ? moment(row.original.lastCommentDate)?.format("DD-MM-YYYY") : "-"}
          </div>
        ),
      },
      {
        header: "Invoice Number",
        id: "invoiceNumber",
        accessorFn: (row) => row.invoiceNumber || "",
        size: 300,
      },
      {
        header: "PO Number",
        id: "poNumber",
        accessorFn: (row) => row.poNumber || "",
        size: 250,
      },
      { header: "Vendor Code", id: "vendorCode", accessorFn: (row) => row.vendorCode || "", size: 300 },
      { header: "PAN", id: "pan", accessorFn: (row) => row.pan || "", size: 300 },
      {
        header: "Checker",
        id: "checker",
        accessorFn: (row) => row.checker?.map((checker) => checker.name).join(", ") || "", // Join names with a comma
        filterFn: "contains",
        size: 300,
      },
      {
        header: "Maker",
        id: "maker",
        accessorFn: (row) => row.maker?.map((maker) => maker.name).join(", ") || "", // Join names with a comma
        filterFn: "contains",
        size: 300,
      },
      {
        header: "Approver",
        id: "approver",
        accessorFn: (row) => row.approver?.map((approver) => approver.name).join(", ") || "", // Join names with a comma
        filterFn: "contains",
        size: 300,
      },
      {
        header: "Ageing category Finance",
        id: "agingCategoryFinance",
        accessorFn: (row) => row.agingCategoryFinance || "",
        filterVariant: "multi-select",
        filterSelectOptions: Object.values(AgingCategories),
        size: 300,
      },
      {
        header: "Ageing category Inwarding",
        id: "agingCategoryInwarding",
        accessorFn: (row) => row.agingCategoryInwarding || "",
        filterVariant: "multi-select",
        filterSelectOptions: Object.values(InwardingAgingCategories),
        size: 300,
      },

      {
        header: "Remarks details",
        id: "remarksDetails",
        accessorFn: (row) => row.remarksDetails || "",
        size: 300,
        Cell: ({ cell }) => {
          const text = cell.getValue() as string;

          return (
            <div
              style={{
                whiteSpace: "nowrap", // Prevent text from wrapping to the next line
                overflow: "hidden", // Hide overflowing text
                textOverflow: "ellipsis", // Add "..." to indicate truncated text
                maxWidth: "100%", // Ensure it respects the column size
                cursor: "pointer", // Indicate interactivity
              }}
              title={text} // Show full text on hover
            >
              {text}
            </div>
          );
        },
      },
      {
        header: "FnV",
        id: "fnvAmount",
        accessorFn: (row) => row.fnvAmount || "",
        size: 250,
        filterVariant: "range",
        filterFn: "betweenInclusive",
        Cell: ({ row }: any) => (
          <div
            className="textOverflow_hidden"
            title={row.original?.fnvAmount ? formatMoney(ToDineroObj(row.original?.fnvAmount)) : ""}
          >
            {row.original?.fnvAmount ? formatMoney(ToDineroObj(row.original?.fnvAmount)) : "-"}
          </div>
        ),
      },
      {
        header: "Non-FnV",
        id: "nonFnvAmount",
        accessorFn: (row) => row.nonFnvAmount || "",
        size: 250,
        filterVariant: "range",
        filterFn: "betweenInclusive",
        Cell: ({ row }: any) => (
          <div
            className="textOverflow_hidden"
            title={row.original?.nonFnvAmount ? formatMoney(ToDineroObj(row.original?.nonFnvAmount)) : ""}
          >
            {row.original?.nonFnvAmount ? formatMoney(ToDineroObj(row.original?.nonFnvAmount)) : "-"}
          </div>
        ),
      },
    ],
    []
  );

  // useEffect(() => {
  //   // setLoader(true);
  //   if (tabValue === 0) {

  //   listOpenInvoices()}
  //   else if (tabValue === 1) {
  //     listCloseInvoices();
  //   }
  // }, [debouncedFilters, sorting]);

  useEffect(() => {
    setLoader(true);

    listOpenInvoices();
    listCloseInvoices();

    listSummaryData();
    listDropdownValues();
    // listCloseInvoices();
    listAllUsers();
  }, []);

  // Page changes are served by the API, so only the visible tab needs refetching. Set when the page
  // has already been fetched by whoever changed it — the mount effect above, or resetPagination.
  const skipPaginationFetch = useRef(true);
  useEffect(() => {
    if (skipPaginationFetch.current) {
      skipPaginationFetch.current = false;
      return;
    }
    if (tabValue === 0) listOpenInvoices(filters, sorting, pagination);
    else listCloseInvoices(filters, sorting, pagination);
  }, [pagination]);

  /**
   * Every filter/sort/tab change invalidates the current page, so go back to page 1. Callers pass
   * the returned value straight into their own fetch, hence the effect above is told to stand down.
   */
  const resetPagination = () => {
    if (pagination !== DEFAULT_PAGINATION) skipPaginationFetch.current = true;
    setPagination(DEFAULT_PAGINATION);
    return DEFAULT_PAGINATION;
  };

  const listSummaryData = () => {
    useFetch(API_ENDPOINTS.FETCH_SUMMARY(role).url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_SUMMARY(role).failureMessage,
      thenCallBack: (response) => {
        setLoader(false);
        if (
          actor.userRole === UserRole.DRApprover ||
          actor.userRole === UserRole.ReconDRApprover ||
          actor.userRole === UserRole.DRChecker
        ) {
          if (response.data.summary) {
            setSummaryData(response.data.summary.summary || {});
          }
        }
      },
      catchCallBack: () => {
        setLoader(false);
      },
    });
  };
  const listDropdownValues = () => {
    useFetch(API_ENDPOINTS.FETCH_INTERNAL_PROCESS_DROPDOWN_VALUES.url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_INTERNAL_PROCESS_DROPDOWN_VALUES.failureMessage,
      thenCallBack: (response) => {
        setAllDropDownValues(response.data.dropDownValues || {});
      },
    });
  };
  const listOpenInvoices = async (filtersArr = filters, sortingArr = sorting, paginationArg = pagination) => {
    setTableLoader(true);
    await useFetch(API_ENDPOINTS.FETCH_OPEN_INVOICES(role).url, "POST", {
      failureMessage: API_ENDPOINTS.FETCH_OPEN_INVOICES(role).failureMessage,
      data: {
        filters: filtersArr,
        sorting: sortingArr,
        page: paginationArg.pageIndex + 1,
        pageSize: paginationArg.pageSize,
      },
      thenCallBack: (response) => {
        setApplyFilterLoader(false);
        setLoader(false);
        setTableLoader(false);
        setRowSelection({});
        setOpenInvoiceCount(response.data.totalCount || 0);
        setRowsDataRejectedInvoicesOpen(response.data.invoices || []);
        if (
          actor.userRole === UserRole.DRApprover ||
          actor.userRole === UserRole.ReconDRApprover ||
          actor.userRole === UserRole.DRChecker
        ) {
          if (response.data.summary) {
            setSummaryData(response.data.summary);
          }
        }
      },
      catchCallBack: () => {
        setLoader(false);
        setTableLoader(false);
        setApplyFilterLoader(false);
      },
    });
  };

  const listCloseInvoices = async (filtersArr = filters, sortingArr = sorting, paginationArg = pagination) => {
    setTableLoader(true);
    await useFetch(API_ENDPOINTS.FETCH_CLOSED_INVOICES(role).url, "POST", {
      failureMessage: API_ENDPOINTS.FETCH_CLOSED_INVOICES(role).failureMessage,
      data: {
        filters: filtersArr,
        sorting: sortingArr,
        page: paginationArg.pageIndex + 1,
        pageSize: paginationArg.pageSize,
      },
      thenCallBack: (response) => {
        setApplyFilterLoader(false);
        setTableLoader(false);
        setRowSelection({});
        setCloseInvoiceCount(response.data.invoices.totalCount || 0);
        setRowsDataRejectedInvoicesClose(response.data.invoices.invoices || []);
      },
      catchCallBack: () => {
        setTableLoader(false);
        setApplyFilterLoader(false);
      },
    });
  };

  const listAllUsers = () => {
    useFetch(API_ENDPOINTS.FETCH_INTERNAL_PROCESS_USERS.url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_INTERNAL_PROCESS_USERS.failureMessage,
      thenCallBack: (response) => {
        setAllUsers(response.data.users || []);
      },
    });
  };

  const downloadInvoiceReport = () => {
    setIsDownloadInvoiceReport(true);

    const tabVal = tabValue === 0 ? "open" : "close";
    useFetch(API_ENDPOINTS.DOWNLOAD_INVOICES(role).url, "POST", {
      failureMessage: API_ENDPOINTS.DOWNLOAD_INVOICES(role).failureMessage,
      data: {
        type: tabVal,
        filter: filters,
      },
      // The report now streams back as a raw .xlsx binary (not base64 JSON), so there is no row
      // ceiling on it — a full 6 lakh row download takes about a minute to arrive.
      config: { responseType: "blob" },
      thenCallBack: (response) => {
        const disposition = response.headers["content-disposition"] ?? "";
        const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] || `Rejected_Invoices_Report_${tabVal}.xlsx`;
        saveAs(response.data as Blob, filename);
        setIsDownloadInvoiceReport(false);
      },
      catchCallBack: () => {
        setIsDownloadInvoiceReport(false);
      },
    });
  };

  const canTakeActionCheck = (row, role) => {
    const issueStatus = row.issueStatus;

    if (role === UserRole.DRMaker) {
      return (
        issueStatus === InternalResolutionStatus.InProgress ||
        issueStatus === InternalResolutionStatus.RejectedAgain ||
        issueStatus === InternalResolutionStatus.PendingApproval ||
        issueStatus === InternalResolutionStatus.UpdatedInViniculum
      );
    } else if (role === UserRole.DRApprover) {
      return (
        issueStatus === InternalResolutionStatus.Unassigned ||
        issueStatus === InternalResolutionStatus.InProgress ||
        issueStatus === InternalResolutionStatus.PendingApproval ||
        issueStatus === InternalResolutionStatus.RejectedAgain ||
        issueStatus === InternalResolutionStatus.UpdatedInViniculum
      );
    } else if (role === UserRole.DRChecker) {
      return (
        issueStatus === InternalResolutionStatus.Unassigned ||
        issueStatus === InternalResolutionStatus.InProgress ||
        issueStatus === InternalResolutionStatus.PendingApproval ||
        issueStatus === InternalResolutionStatus.RejectedAgain ||
        issueStatus === InternalResolutionStatus.UpdatedInViniculum
      );
    }

    return false; // Default case: no action allowed
  };

  return (
    <LoggedInSkeleton topBarButtons={getReconTopBarButtons(" ", actor.name, location?.state?.openCollapseObj, actor)}>
      <Suspense fallback={<LoadingPage />}>
        {!loader ? (
          <>
            <Grid sx={{ minHeight: "100vh" }}>
              {(actor.userRole === UserRole.DRApprover ||
                actor.userRole === UserRole.ReconDRApprover ||
                actor.userRole === UserRole.DRChecker) && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                    padding: "12px 16px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (tabValue === 0) {
                          setFilters([]);
                          setSorting([]);
                          listOpenInvoices([], [], resetPagination());
                        } else if (tabValue === 1 && actor.userRole === UserRole.DRChecker) {
                          setFilters([]);
                          setSorting([]);
                          listCloseInvoices([], [], resetPagination());
                        }
                        uiLogger(uiLoggerNamesInvoiceRejection.UI_SST_IR_SUMMARY_CLICK.functionName, null, null, {
                          message: uiLoggerNamesInvoiceRejection.UI_SST_IR_SUMMARY_CLICK.message,
                        });
                      }}
                    >
                      <Typography className="heading-m">Total</Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: "#1976d2",
                        }}
                      >
                        <Typography className="heading-m">
                          {formatMoney(ToDineroObj(summaryData?.totalInvoiceAmount))} ({summaryData?.totalInvoiceCount})
                        </Typography>
                        {/* <InfoOutlined sx={{ marginLeft: '4px', width: '20px', height: '20px' }} /> */}
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "12px",
                      width: "100%",
                    }}
                  >
                    {summaryData?.statusWiseSummary?.map((card, index) => (
                      <Card
                        key={index}
                        sx={{
                          flex: 1,
                          border: "1.18px solid #e4e4e4",
                          borderRadius: "4.74px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          if (actor.userRole === UserRole.DRChecker) {
                            if (
                              card.title === InternalResolutionStatus.PendingApproval ||
                              card.title === InternalResolutionStatus.InvoiceProcessing
                            ) {
                              setTabValue(1);
                              const issueStatuses =
                                card.title === InternalResolutionStatus.PendingApproval
                                  ? [
                                      InternalResolutionStatus.PendingApproval,
                                      InternalResolutionStatus.UpdatedInViniculum,
                                    ]
                                  : [card.title];

                              const updatedFilters = [{ id: "issueStatus", value: issueStatuses }];
                              setFilters(updatedFilters);
                              listCloseInvoices(updatedFilters, sorting, resetPagination());
                            } else if (
                              [
                                InternalResolutionStatus.InProgress,
                                InternalResolutionStatus.RejectedAgain,
                                InternalResolutionStatus.Unassigned,
                              ].includes(card.title)
                            ) {
                              setTabValue(0);
                              const issueStatuses =
                                card.title === InternalResolutionStatus.InProgress
                                  ? [InternalResolutionStatus.InProgress, InternalResolutionStatus.RejectedAgain]
                                  : [card.title];

                              const updatedFilters = [{ id: "issueStatus", value: issueStatuses }];
                              setFilters(updatedFilters);
                              listOpenInvoices(updatedFilters, sorting, resetPagination());
                            }
                          } else if (
                            actor.userRole === UserRole.DRApprover ||
                            actor.userRole === UserRole.ReconDRApprover
                          ) {
                            setTabValue(0);
                            const issueStatuses =
                              card.title === InternalResolutionStatus.InProgress
                                ? [InternalResolutionStatus.InProgress, InternalResolutionStatus.RejectedAgain]
                                : card.title === InternalResolutionStatus.PendingApproval
                                ? [
                                    InternalResolutionStatus.PendingApproval,
                                    InternalResolutionStatus.UpdatedInViniculum,
                                  ]
                                : [card.title];

                            const updatedFilters = [{ id: "issueStatus", value: issueStatuses }];
                            setFilters(updatedFilters);
                            listOpenInvoices(updatedFilters, sorting, resetPagination());
                          }

                          uiLogger(uiLoggerNamesInvoiceRejection.UI_SST_IR_SUMMARY_CLICK.functionName, null, null, {
                            message: uiLoggerNamesInvoiceRejection.UI_SST_IR_SUMMARY_CLICK.message,
                          });
                        }}
                      >
                        <CardContent
                          sx={{
                            padding: "20px !important",
                          }}
                        >
                          <Typography
                            className="heading-xxs"
                            sx={{
                              color: "#717171",
                              marginBottom: "8px",
                            }}
                          >
                            {card.title}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Typography className="heading-s">{formatMoney(ToDineroObj(card.amount))}</Typography>
                            <Typography className="heading-s">({card.count})</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}

              <Paper sx={{ mt: 2 }}>
                <Box className="space_between" sx={{ padding: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}>
                  <Box>
                    <div className="heading-m">
                      {actor.userRole === UserRole.DRApprover ||
                      actor.userRole === UserRole.ReconDRApprover ||
                      actor.userRole === UserRole.DRChecker
                        ? "Disputed Entries"
                        : `You have ${openInvoiceCount} pending issues`}
                    </div>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <CustomButton
                      size="medium"
                      colorToken="violet"
                      variant="contained"
                      disabled={Object.values(rowSelection).filter(Boolean).length === 0}
                      onClick={() => {
                        setOpenBulkActionOfRejectedInvoices(true);
                        uiLogger(uiLoggerNamesInvoiceRejection.UI_SST_IR_BULKACTIONS_CLICK.functionName, null, null, {
                          message: uiLoggerNamesInvoiceRejection.UI_SST_IR_BULKACTIONS_CLICK.message,
                        });
                      }}
                    >
                      Bulk Actions
                    </CustomButton>

                    <CustomButton
                      size="medium"
                      colorToken="violet"
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={() => {
                        setOpenFilterOfRejectedInvoices(true);
                        uiLogger(uiLoggerNamesInvoiceRejection.UI_SST_IR_FILTER_CLICK.functionName, null, null, {
                          message: uiLoggerNamesInvoiceRejection.UI_SST_IR_FILTER_CLICK.message,
                        });
                      }}
                    >
                      Filter
                    </CustomButton>
                    <CustomButton
                      size="medium"
                      colorToken="violet"
                      variant="outlined"
                      onClick={() => {
                        downloadInvoiceReport();
                      }}
                      disabled={isDownloadInvoiceReport}
                    >
                      {isDownloadInvoiceReport ? (
                        <CircularProgress size={24} color="inherit" /> // Show loading icon
                      ) : (
                        <Download /> // Show download icon
                      )}
                    </CustomButton>
                  </Box>
                </Box>
                <Box sx={{ borderBottom: 1, borderColor: "divider", marginTop: 1 }}>
                  <Tabs
                    value={tabValue}
                    onChange={(e, val) => {
                      e.preventDefault();
                      setFilters([]);
                      setSorting([]);
                      setTabValue(val);
                      setClearRows(true);
                      setRowSelection({});

                      if (val === 0) {
                        listOpenInvoices([], [], resetPagination());
                      } else {
                        listCloseInvoices([], [], resetPagination());
                      }
                    }}
                    sx={{
                      minHeight: 44,
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#531c4c",
                      },
                      "& .MuiChip-root": {
                        color: "#fff",
                        bgcolor: "#0009",
                        px: "6.5px",
                        py: 0,
                      },
                      "& .Mui-selected": {
                        color: "#531c4c !important",
                        "& .MuiChip-root": {
                          bgcolor: "#531c4c",
                        },
                      },
                    }}
                  >
                    <Tab
                      label="Pending"
                      icon={<Chip size="small" label={openInvoiceCount} />}
                      iconPosition="end"
                      sx={{ textTransform: "none", minHeight: "auto" }}
                    />
                    <Tab
                      label="Completed"
                      icon={<Chip size="small" label={closeInvoiceCount} />}
                      iconPosition="end"
                      sx={{ textTransform: "none", minHeight: "auto" }}
                    />
                  </Tabs>
                </Box>

                <Box>
                  <ToggleButtonGroup
                    color="primary"
                    value={
                      filters.find((f) => f.id === "priority")?.value?.[0] === RejectedInvoicesPriority.High
                        ? QuickFilterOptions.HighPriority
                        : filters.find((f) => f.id === "issueStatus")?.value?.[0] ===
                          InternalResolutionStatus.RejectedAgain
                        ? QuickFilterOptions.RejectedAgain
                        : QuickFilterOptions.All
                    }
                    exclusive
                    onChange={(_e, val) => {
                      // setSelectedQF(val);
                      if (val === QuickFilterOptions.All) {
                        setFilters([]);
                        if (tabValue === 0) {
                          listOpenInvoices([], [], resetPagination());
                        } else {
                          listCloseInvoices([], [], resetPagination());
                        }
                      } else if (val === QuickFilterOptions.RejectedAgain) {
                        const updatedFilters = [{ id: "issueStatus", value: [InternalResolutionStatus.RejectedAgain] }];
                        setFilters(updatedFilters);
                        if (tabValue === 0) {
                          listOpenInvoices(updatedFilters, sorting, resetPagination());
                        } else {
                          listCloseInvoices(updatedFilters, sorting, resetPagination());
                        }
                      } else if (val === QuickFilterOptions.HighPriority) {
                        const updatedFilters = [{ id: "priority", value: [RejectedInvoicesPriority.High] }];
                        setFilters(updatedFilters);
                        if (tabValue === 0) {
                          listOpenInvoices(updatedFilters, sorting, resetPagination());
                        } else {
                          listCloseInvoices(updatedFilters, sorting, resetPagination());
                        }
                      }
                    }}
                    sx={{
                      gap: 1,
                      px: 2,
                      py: 1.5,
                      "& .MuiToggleButton-root": {
                        borderRadius: "100px !important",
                        padding: "4px 12px",
                        borderLeft: "1px solid #0002 !important",
                        textTransform: "Capitalize",

                        "&.Mui-selected": {
                          background: "#EEE8ED",
                          color: "#531c4c",

                          "&:hover": {
                            background: "#EEE8F4",
                          },
                        },
                      },
                    }}
                  >
                    {quickFilterOptions.map((value: string) => (
                      <ToggleButton key={value} value={value}>
                        {value}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>

                  {filters?.some((f) =>
                    Array.isArray(f.value)
                      ? f.value.some((v) => v !== null && v !== undefined && v !== "")
                      : f.value !== null && f.value !== undefined && f.value !== ""
                  ) && <RejectedInvoicesFiltersBars filters={filters} setFilters={setFilters} />}
                  <ReactTable
                    columns={columnDefinitionRejectedInvoices}
                    // rows={finalQuickFilteredRejectedInvoices}
                    rows={tabValue === 0 ? rowsDataRejectedInvoicesOpen : rowsDataRejectedInvoicesClose}
                    manualSorting
                    manualFiltering
                    manualPagination
                    rowCount={(tabValue === 0 ? openInvoiceCount : closeInvoiceCount) || 0}
                    onSortingChange={setSorting}
                    onColumnFiltersChange={setFilters}
                    onPaginationChange={setPagination}
                    state={{ sorting, columnFilters: filters, rowSelection, pagination, isLoading: tableLoader }}
                    onRowSelectionChange={setRowSelection}
                    getRowId={(row) => String(row.grnNumber)} // must match what's in rowSelection
                    enableColumnResizing={true}
                    loading={tableLoader}
                    enableGlobalFilter={false} // 🔒 disables the search box completely
                    internalActionsOrder={[
                      "filterToggle", // filter icon
                      "showHideColumns", // column visibility icon
                      "fullScreenToggle", // fullscreen icon
                    ]}
                    renderTopToolbarCustomActions={() => (
                      <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                        <CustomButton
                          size="medium"
                          colorToken="violet"
                          variant="outlined"
                          disabled={applyFilterLoader}
                          startIcon={<LoadingIcon loading={applyFilterLoader} />}
                          onClick={() => {
                            setApplyFilterLoader(true);
                            tabValue === 0
                              ? listOpenInvoices(filters, sorting, resetPagination())
                              : listCloseInvoices(filters, sorting, resetPagination());
                          }} // your custom filter logic
                          sx={{ ml: 2 }}
                        >
                          Apply Filter
                        </CustomButton>
                      </Box>
                    )}
                    enableGrouping={true}
                    positionToolbarAlertBanner={"top"}
                    enablePagination={true}
                    // Only the current page is in the browser, so "select all" can only mean this page.
                    pageSelectMode={true}
                    enableRowSelection={true}
                    clearRowsSelection={clearRows}
                    setClearRowsSelection={setClearRows}
                    displayColumnDefOptions={{
                      "mrt-row-actions": {
                        size: 250, //if using layoutMode that is not 'semantic', the columns will not auto-size, so you need to set the size manually
                        grow: false,
                      },
                    }}
                    enableRowActions
                    renderRowActions={({ row }) => {
                      const canTakeAction = canTakeActionCheck(row.original, actor?.userRole);

                      // State for the 3-dot menu
                      const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
                      const open = Boolean(anchorEl);

                      const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
                        setAnchorEl(event.currentTarget);
                      };

                      const handleMenuClose = () => {
                        setAnchorEl(null);
                      };

                      return (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "nowrap",
                            gap: "8px",
                            justifyContent: "center",
                            padding: "11px 11px 8px 11px",
                          }}
                        >
                          <CustomButton
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ width: "130px" }}
                            onClick={() => {
                              selectedRowForRejectedInvoicesResolveModal.current = row.original;
                              setIsCommentModalOpen(false);
                              setOpenRejectedInvoicesResolveModal(true);
                              uiLogger(
                                uiLoggerNamesInvoiceRejection.UI_SST_IR_TAKEACTION_CLICK.functionName,
                                null,
                                null,
                                {
                                  message: uiLoggerNamesInvoiceRejection.UI_SST_IR_TAKEACTION_CLICK.message,
                                }
                              );
                            }}
                          >
                            {canTakeAction ? "Take Action" : "View Details"} {/* Dynamic button text */}
                          </CustomButton>
                          <IconButton
                            onClick={() => {
                              selectedRowForRejectedInvoicesResolveModal.current = row.original;
                              setIsCommentModalOpen(true);
                              setOpenRejectedInvoicesResolveModal(true);
                              uiLogger(
                                uiLoggerNamesInvoiceRejection.UI_SST_IR_TAKEACTION_COMMENTS_ADD_CLICK.functionName,
                                null,
                                null,
                                {
                                  message:
                                    uiLoggerNamesInvoiceRejection.UI_SST_IR_TAKEACTION_COMMENTS_ADD_CLICK.message,
                                }
                              );
                            }}
                          >
                            {row.original.hasNewComments ? ( // Replace `true` with your condition
                              <Badge color="error" badgeContent=" " variant="dot">
                                <ChatBubbleIcon />
                              </Badge>
                            ) : (
                              <ChatBubbleIcon />
                            )}
                          </IconButton>

                          {/* 3-dot menu */}
                          <IconButton onClick={handleMenuOpen}>
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                          >
                            {actor.userRole !== UserRole.DRMaker && (
                              <MenuItem
                                onClick={() => {
                                  handleMenuClose();

                                  setSelectedRowForEdit(row.original);
                                  setOpenEditDetailsOfRejectedInvoices(true);

                                  uiLogger(
                                    uiLoggerNamesInvoiceRejection.UI_SST_IR_EDITDETAILS_CLICK.functionName,
                                    null,
                                    null,
                                    {
                                      message: uiLoggerNamesInvoiceRejection.UI_SST_IR_EDITDETAILS_CLICK.message,
                                    }
                                  );
                                }}
                              >
                                Edit Details
                              </MenuItem>
                            )}
                            <MenuItem
                              onClick={() => {
                                handleMenuClose();
                                setOpenHistoryOfRejectedInvoices(true);
                                setSelectedRowForHistory(row.original);
                                uiLogger(
                                  uiLoggerNamesInvoiceRejection.UI_SST_IR_VIEWHISTORY_CLICK.functionName,
                                  null,
                                  null,
                                  {
                                    message: uiLoggerNamesInvoiceRejection.UI_SST_IR_VIEWHISTORY_CLICK.message,
                                  }
                                );
                              }}
                            >
                              View History
                            </MenuItem>
                          </Menu>
                        </Box>
                      );
                    }}
                    renderBottomToolbarCustomActions={() => {
                      return <div> </div>;
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </>
        ) : (
          <Grid sx={{ minHeight: "100vh", padding: 2 }}>
            {actor.userRole !== UserRole.DRMaker && (
              <Paper sx={{ padding: 2, marginBottom: 2 }}>
                <Skeleton variant="text" width="30%" height={60} />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Skeleton variant="rectangular" width="25%" height={150} />
                  <Skeleton variant="rectangular" width="25%" height={150} />
                  <Skeleton variant="rectangular" width="25%" height={150} />
                  <Skeleton variant="rectangular" width="25%" height={150} />
                </Box>
              </Paper>
            )}
            <Paper sx={{ padding: 2 }}>
              <Skeleton variant="text" width="30%" height={60} />
              <Skeleton variant="rectangular" width="100%" height={400} sx={{ marginTop: 2 }} />
            </Paper>
          </Grid>
        )}
      </Suspense>
      {openRejectedInvoicesResolveModal && (
        <RejectedInvoicesTakeActionModal
          openRejectedInvoicesResolveModal={openRejectedInvoicesResolveModal}
          setOpenRejectedInvoicesResolveModal={setOpenRejectedInvoicesResolveModal}
          selectedRowForRejectedInvoicesResolveModal={selectedRowForRejectedInvoicesResolveModal.current}
          // rejectedInvoicesResolveModalTitle={rejectedInvoicesResolveModalTitle}
          role={role}
          listSummaryData={listSummaryData}
          listOpenInvoices={listOpenInvoices}
          listCloseInvoices={listCloseInvoices}
          isCommentModalOpen={isCommentModalOpen}
          setIsCommentModalOpen={setIsCommentModalOpen}
        />
      )}
      {openEditDetailsOfRejectedInvoices && (
        <EditDetailsOfRejectedInvoices
          allUsers={allUsers || []}
          openEditDetailsOfRejectedInvoices={openEditDetailsOfRejectedInvoices}
          setOpenEditDetailsOfRejectedInvoices={setOpenEditDetailsOfRejectedInvoices}
          selectedRowForEdit={selectedRowForEdit}
          role={role}
          listOpenInvoices={listOpenInvoices}
          listCloseInvoices={listCloseInvoices}
        />
      )}
      {openHistoryOfRejectedInvoices && (
        <HistoryOfRejectedInvoices
          openHistoryOfRejectedInvoices={openHistoryOfRejectedInvoices}
          setOpenHistoryOfRejectedInvoices={setOpenHistoryOfRejectedInvoices}
          selectedRowForHistory={selectedRowForHistory}
          role={role}
        />
      )}
      {openBulkActionOfRejectedInvoices && (
        <BulkActionOfRejectedInvoices
          openBulkActionOfRejectedInvoices={openBulkActionOfRejectedInvoices}
          setOpenBulkActionOfRejectedInvoices={setOpenBulkActionOfRejectedInvoices}
          // selectedRow={selectedRow}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          allUsers={allUsers}
          role={role}
          listSummaryData={listSummaryData}
          listOpenInvoices={listOpenInvoices}
          listCloseInvoices={listCloseInvoices}
          filters={filters}
        />
      )}
      {openFilterOfRejectedInvoices && (
        <FilterOfRejectedInvoices
          openFilterOfRejectedInvoices={openFilterOfRejectedInvoices}
          setOpenFilterOfRejectedInvoices={setOpenFilterOfRejectedInvoices}
          filters={filters}
          setFilters={setFilters}
          tabValue={tabValue}
          listOpenInvoices={listOpenInvoices}
          listCloseInvoices={listCloseInvoices}
          allDropDownValues={allDropDownValues}
        />
      )}
    </LoggedInSkeleton>
  );
};

export default RejectedInvoices;
